import React, { lazy, Suspense } from 'react';
import { Helmet } from 'react-helmet-async';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaTrophy, FaTools, FaHardHat, FaThumbsUp, FaChevronDown, 
  FaHome, FaBuilding, FaTractor, FaTree, FaWrench, FaCheck, FaArrowRight, FaSpinner } from 'react-icons/fa';

// Lazy load the service details section
const ServiceDetails = lazy(() => import('../components/services/ServiceDetailsSection'));

// Services data
const services = [
  {
    id: 'residential-demolition',
    title: 'Residential Demolition',
    description: 'Complete demolition of houses, garages, sheds, pools, and other residential structures. We handle all permits, utilities disconnection, and debris removal.',
    fullDescription: `Our residential demolition services provide comprehensive solutions for homeowners looking to remove existing structures. 
    From complete house demolition to removing specific structures like garages, sheds, or pools, our team has the expertise to handle projects of any size.
    We manage the entire process including obtaining necessary permits, disconnecting utilities safely, and complete debris removal and disposal.`,
    icon: <FaHome />,
    features: [
      'Complete structure removal',
      'Partial home demolition',
      'Garage and shed demolition',
      'Pool removal',
      'Permit acquisition',
      'Utility disconnection',
      'Complete debris removal'
    ]
  },
  {
    id: 'commercial-demolition',
    title: 'Commercial Demolition',
    description: 'Professional demolition services for commercial buildings, retail spaces, and offices. We ensure safe, efficient, and environmentally responsible demolition.',
    fullDescription: `Our commercial demolition team specializes in the safe, efficient removal of commercial structures including office buildings, retail spaces, warehouses, and industrial facilities.
    We understand the unique challenges of commercial demolition projects and implement careful planning to minimize disruption to surrounding businesses. Our advanced equipment and experienced crew ensure your project is completed on time and within budget.`,
    icon: <FaBuilding />,
    features: [
      'Office building demolition',
      'Retail space removal',
      'Warehouse demolition',
      'Industrial facility removal',
      'Structural dismantling',
      'Hazardous material handling',
      'Recycling and waste management'
    ]
  },
  {
    id: 'site-clearing',
    title: 'Site Clearing & Preparation',
    description: 'Complete land clearing, brush removal, grading, and site preparation for construction or landscaping projects. We prepare your property for its next phase.',
    fullDescription: `Our comprehensive site clearing services prepare your land for its next phase of development. We handle everything from vegetation removal to grading and final preparation for construction.
    Our team uses specialized equipment to efficiently clear brush, remove trees and stumps, grade the terrain, and prepare the soil for building or landscaping. We prioritize environmentally responsible practices while delivering exceptional results.`,
    icon: <FaTractor />,
    features: [
      'Land clearing',
      'Brush and vegetation removal',
      'Grading and leveling',
      'Soil preparation',
      'Erosion control',
      'Construction site preparation',
      'Stump and root removal'
    ]
  },
  {
    id: 'concrete-removal',
    title: 'Concrete Removal',
    description: 'Removal and disposal of concrete driveways, patios, foundations, and slabs. We use specialized equipment to efficiently break up and haul away concrete.',
    fullDescription: `Our concrete removal services provide efficient solutions for removing driveways, sidewalks, patios, foundations, and slabs. Our specialized equipment and experienced team can handle concrete of any thickness or reinforcement.
    We take care of breaking up the concrete, removing all debris, and preparing the area for new construction or landscaping. Our methods minimize disruption to surrounding structures and landscaping.`,
    icon: <FaHardHat />,
    features: [
      'Driveway removal',
      'Patio and sidewalk demolition',
      'Foundation removal',
      'Concrete slab removal',
      'Reinforced concrete handling',
      'Complete debris removal',
      'Site preparation after removal'
    ]
  },
  {
    id: 'tree-removal',
    title: 'Tree & Stump Removal',
    description: 'Safe removal of trees, stumps, and root systems to clear your property. We have the equipment and expertise to handle trees of all sizes.',
    fullDescription: `Our professional tree and stump removal services safely eliminate unwanted trees and stumps from your property. We handle trees of all sizes, from small ornamentals to large mature trees that pose risks to structures.
    Our specialized equipment and trained crew ensure that trees are removed safely without damage to surrounding property. We also provide complete stump grinding and root removal to fully clear your land for future use.`,
    icon: <FaTree />,
    features: [
      'Large tree removal',
      'Hazardous tree elimination',
      'Complete stump grinding',
      'Root system removal',
      'Emergency tree removal',
      'Property protection measures',
      'Debris chipping and removal'
    ]
  },
  {
    id: 'selective-demolition',
    title: 'Selective & Interior Demolition',
    description: 'Precise removal of specific parts of structures while preserving the rest. Perfect for remodels, renovations, and interior demolition projects.',
    fullDescription: `Our selective demolition services provide precise removal of specific elements within a structure while preserving the rest. This service is ideal for renovation projects, remodels, or when only certain components need removal.
    Our experienced team carefully dismantles interior walls, fixtures, flooring, or other targeted elements with minimal disruption to the remaining structure. We implement dust control measures and ensure safe removal of all materials.`,
    icon: <FaWrench />,
    features: [
      'Interior wall removal',
      'Kitchen and bathroom demolition',
      'Floor and ceiling removal',
      'Fixture and cabinet removal',
      'Dust containment systems',
      'Structural preservation',
      'Material sorting for recycling'
    ]
  }
];

const Services = () => {
  const scrollToContent = () => {
    const contentSection = document.getElementById('services-content');
    if (contentSection) {
      contentSection.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <>
      <Helmet>
        <title>Our Premium Services | Florida's Top Site Service & Demolition</title>
        <meta name="description" content="Professional demolition and site clearing services in Crescent City, Florida. Comprehensive residential and commercial solutions with over 15 years of experience." />
        <meta name="keywords" content="demolition services, site clearing, concrete removal, tree removal, construction site preparation, residential demolition, commercial demolition, Crescent City, Florida" />
        <link rel="canonical" href="https://www.floridastopsite.com/services" />
        
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Premium Services | Florida's Top Site Service & Demolition" />
        <meta property="og:description" content="Professional demolition and site clearing services in Crescent City, Florida. Comprehensive residential and commercial solutions with over 15 years of experience." />
        <meta property="og:url" content="https://www.floridastopsite.com/services" />
        <meta property="og:image" content="https://www.floridastopsite.com/images/og-image.jpg" />
      </Helmet>
      
      <HeroSection>
        <div className="container">
          <HeroContent>
            <h1>Professional Demolition & Clearing Services</h1>
            <p>Precision demolition with cutting-edge technology and 15+ years of expertise</p>
            <ScrollButton onClick={scrollToContent}>
              Explore Our Services <FaChevronDown />
            </ScrollButton>
          </HeroContent>
        </div>
      </HeroSection>
      
      <MainContainer id="services-content">
        <section className="services-intro">
          <div className="container">
            <SectionTitle>
              <span>What We Offer</span>
              <h2>Premium Demolition Services</h2>
              <p>Service-based business – we come to you!</p>
            </SectionTitle>
            
            <IntroText>
              <p>At Florida's Top Site Service & Demolition, we provide a comprehensive range of <strong>high-end demolition and site clearing services</strong> for residential and commercial clients throughout Crescent City and surrounding areas in Florida. With over 15 years of experience, we deliver professional, reliable, and safe solutions for your property improvement projects.</p>
              <p>From complete structure demolition to selective interior removal, our team has the expertise and state-of-the-art equipment to handle projects of any size and complexity. We take pride in our environmentally responsible practices, ensuring proper disposal and recycling of materials whenever possible.</p>
            </IntroText>
          </div>
        </section>
        
        <ServicesSection>
          <div className="container">
            <ServicesGrid>
              {services.map((service) => (
                <ServiceCard key={service.id} id={service.id}>
                  <ServiceIcon>{service.icon}</ServiceIcon>
                  <h3>{service.title}</h3>
                  <p>{service.description}</p>
                  <a href={`#${service.id}-details`} className="learn-more">
                    Learn More <FaArrowRight />
                  </a>
                </ServiceCard>
              ))}
            </ServicesGrid>
          </div>
        </ServicesSection>
        
        <WhyChooseUsSection>
          <div className="container">
            <SectionTitle light>
              <span>Why Choose Us</span>
              <h2>The Demolition Experts</h2>
              <p>Experience the difference of working with professionals</p>
            </SectionTitle>
            
            <BenefitsGrid>
              <BenefitCard>
                <BenefitIcon>
                  <FaTools />
                </BenefitIcon>
                <h3>Advanced Equipment</h3>
                <p>Our team utilizes the latest demolition technology and equipment to ensure efficiency and precision on every project.</p>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <FaHardHat />
                </BenefitIcon>
                <h3>Licensed & Insured</h3>
                <p>We are fully licensed and insured, providing you with peace of mind and complete protection throughout your project.</p>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <FaTrophy />
                </BenefitIcon>
                <h3>Award-Winning Service</h3>
                <p>Our commitment to excellence has earned us recognition for delivering superior quality workmanship on every job.</p>
              </BenefitCard>
              
              <BenefitCard>
                <BenefitIcon>
                  <FaThumbsUp />
                </BenefitIcon>
                <h3>Eco-Friendly Practices</h3>
                <p>We implement environmentally responsible demolition methods, with material recycling and proper waste management.</p>
              </BenefitCard>
            </BenefitsGrid>
          </div>
        </WhyChooseUsSection>
        
        <Suspense fallback={
          <LoadingSection>
            <FaSpinner className="spinner" />
            <p>Loading service details...</p>
          </LoadingSection>
        }>
          <ServiceDetails services={services} />
        </Suspense>
        
        <CallToActionSection>
          <div className="container">
            <CTABox>
              <h2>Ready to Transform Your Property?</h2>
              <p>Contact us today for a premium consultation and personalized quote for your project.</p>
              <CTAButtons>
                <PrimaryButton as={Link} to="/contact#quote-form">Get a Free Quote</PrimaryButton>
                <SecondaryButton href="tel:+13868722144">(386) 872-2144</SecondaryButton>
              </CTAButtons>
            </CTABox>
          </div>
        </CallToActionSection>
      </MainContainer>
    </>
  );
};

// Styled components
const HeroSection = styled.section`
  height: 70vh;
  min-height: 500px;
  background: linear-gradient(rgba(0, 0, 0, 0.7), rgba(0, 0, 0, 0.7)), 
    url('/images/hero-bg.jpg') center/cover no-repeat;
  display: flex;
  align-items: center;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: radial-gradient(circle at center, rgba(255, 140, 0, 0.2) 0%, rgba(0, 0, 0, 0) 70%);
  }
`;

const HeroContent = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto;
  padding: 2rem;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 10px;
  position: relative;
  z-index: 2;
  
  h1 {
    color: white;
    font-size: 4.5rem;
    margin-bottom: 1.5rem;
    text-shadow: 0 2px 5px rgba(0, 0, 0, 0.5);
    
    @media (max-width: 768px) {
      font-size: 3.2rem;
    }
  }
  
  p {
    font-size: 2rem;
    color: white;
    margin-bottom: 2.5rem;
    
    @media (max-width: 768px) {
      font-size: 1.6rem;
    }
  }
`;

const ScrollButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  color: white;
  border: 2px solid var(--primary-color);
  padding: 1rem 2.5rem;
  font-size: 1.6rem;
  border-radius: 50px;
  cursor: pointer;
  margin: 0 auto;
  transition: all 0.3s ease;
  
  svg {
    margin-left: 1rem;
  }
  
  &:hover {
    background-color: var(--primary-color);
    transform: translateY(-5px);
  }
`;

const MainContainer = styled.main`
  background-color: var(--light-bg);
  color: var(--dark-text);
  
  section {
    padding: 6rem 0;
    
    @media (max-width: 768px) {
      padding: 4rem 0;
    }
  }
  
  .services-intro {
    background-color: var(--light-bg);
    
    h2, p {
      color: var(--dark-text);
    }
  }
  
  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 2rem;
  }
`;

const SectionTitle = styled.div`
  text-align: center;
  margin-bottom: 4rem;
  
  span {
    font-size: 1.8rem;
    text-transform: uppercase;
    letter-spacing: 2px;
    color: var(--primary-color);
    display: block;
    margin-bottom: 1rem;
    font-weight: 600;
  }
  
  h2 {
    font-size: 3.6rem;
    color: ${props => props.light ? 'white' : '#222'};
    margin-bottom: 1rem;
    
    @media (max-width: 768px) {
      font-size: 2.8rem;
    }
  }
  
  p {
    font-size: 1.8rem;
    color: ${props => props.light ? 'rgba(255, 255, 255, 0.9)' : '#555'};
    max-width: 700px;
    margin: 0 auto;
  }
`;

const IntroText = styled.div`
  max-width: 800px;
  margin: 0 auto;
  text-align: center;
  
  p {
    color: var(--dark-text);
    font-size: 1.8rem;
    margin-bottom: 1.5rem;
  }
`;

const ServicesSection = styled.section`
  background-color: var(--light-bg);
  padding: 5rem 0;
  
  h2, h3 {
    color: var(--dark-text);
  }
  
  p {
    color: var(--dark-text);
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceCard = styled.div`
  background-color: var(--card-bg);
  padding: 3rem;
  border-radius: 10px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.1);
  text-align: center;
  transition: all 0.3s ease;
  
  h3 {
    font-size: 2.4rem;
    margin: 1.5rem 0 1rem;
    color: var(--dark-text);
  }
  
  p {
    color: var(--dark-text);
    font-size: 1.6rem;
    margin-bottom: 2rem;
  }
  
  .learn-more {
    display: inline-flex;
    align-items: center;
    color: var(--primary-color);
    font-weight: 600;
    font-size: 1.6rem;
    
    svg {
      margin-left: 0.5rem;
      transition: transform 0.3s ease;
    }
    
    &:hover svg {
      transform: translateX(5px);
    }
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0, 0, 0, 0.15);
  }
`;

const ServiceIcon = styled.div`
  width: 7rem;
  height: 7rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 5px 15px rgba(255, 140, 0, 0.3);
  
  svg {
    color: white;
    font-size: 3rem;
  }
`;

const WhyChooseUsSection = styled.section`
  background: linear-gradient(rgba(0, 0, 0, 0.85), rgba(0, 0, 0, 0.85)), 
    url('/images/hero-bg.jpg') center/cover fixed no-repeat;
  color: white;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 3rem;
  
  @media (max-width: 992px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 3rem 2.5rem;
  border-radius: 10px;
  text-align: center;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.2);
  
  &:hover {
    transform: translateY(-10px);
    background: rgba(255, 255, 255, 0.15);
  }
  
  h3 {
    font-size: 2.2rem;
    color: white;
    margin: 1.5rem 0 1rem;
  }
  
  p {
    font-size: 1.6rem;
    color: rgba(255, 255, 255, 0.9);
    line-height: 1.6;
  }
`;

const BenefitIcon = styled.div`
  width: 7rem;
  height: 7rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  box-shadow: 0 5px 15px rgba(255, 140, 0, 0.3);
  
  svg {
    color: white;
    font-size: 3rem;
  }
`;

const LoadingSection = styled.div`
  padding: 6rem 0;
  text-align: center;
  
  .spinner {
    font-size: 4rem;
    color: var(--primary-color);
    animation: spin 1s linear infinite;
    margin-bottom: 2rem;
  }
  
  p {
    font-size: 1.8rem;
    color: #666;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const CallToActionSection = styled.section`
  background-color: #f8f8f8;
`;

const CTABox = styled.div`
  background: linear-gradient(135deg, #333 0%, #111 100%);
  border-radius: 10px;
  padding: 5rem;
  text-align: center;
  color: white;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  h2 {
    font-size: 3.6rem;
    color: white;
    margin-bottom: 1.5rem;
    
    @media (max-width: 768px) {
      font-size: 2.8rem;
    }
  }
  
  p {
    font-size: 1.8rem;
    color: rgba(255, 255, 255, 0.9);
    margin-bottom: 3rem;
    max-width: 700px;
    margin-left: auto;
    margin-right: auto;
  }
  
  @media (max-width: 576px) {
    padding: 3rem 2rem;
  }
`;

const CTAButtons = styled.div`
  display: flex;
  gap: 2rem;
  justify-content: center;
  
  @media (max-width: 576px) {
    flex-direction: column;
    gap: 1.5rem;
  }
`;

const ButtonBase = styled.a`
  display: inline-block;
  padding: 1.5rem 3rem;
  font-size: 1.7rem;
  font-weight: 600;
  border-radius: 50px;
  text-align: center;
  transition: all 0.3s ease;
  cursor: pointer;
`;

const PrimaryButton = styled(ButtonBase)`
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--secondary-color) 100%);
  color: white;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(255, 140, 0, 0.3);
  }
`;

const SecondaryButton = styled(ButtonBase)`
  background: transparent;
  color: white;
  border: 2px solid var(--primary-color);
  
  &:hover {
    background-color: rgba(255, 140, 0, 0.1);
    transform: translateY(-5px);
  }
`;

export default Services; 